/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Higher order component for wrapping in an error boundary
 */

import hoistStatics from 'hoist-non-react-statics'
import React, { ComponentType, useMemo } from 'react'
import {
  ErrorBoundary,
  ErrorBoundaryPropsWithComponent,
  ErrorBoundaryPropsWithRender,
} from 'react-error-boundary'
import {
  GetProps,
  InferableComponentEnhancerWithProps,
  Matching,
} from 'react-redux'

import { logger } from './logger'

const DefaultErrorFallback = () => null

export interface WithErrorBoundaryArgs {
  displayName?: string
  ErrorFallback?: React.ComponentType<any>
  errorCallback?: (error: Error, errorInfo?: string | null, ...args: any) => any
  FallbackRender?: React.ComponentType<any>
}

export interface WithErrorBoundaryNeedsProps {
  onError?: (
    error: Error,
    errorInfo: React.ErrorInfo,
    forwardProps: object
  ) => void
}

export const withErrorBoundary =
  ({
    displayName,
    errorCallback,
    ErrorFallback = DefaultErrorFallback,
    FallbackRender,
  }: WithErrorBoundaryArgs = {}): InferableComponentEnhancerWithProps<
    {},
    WithErrorBoundaryNeedsProps
  > =>
  // @ts-expect-error
  <C extends ComponentType<Matching<WithErrorBoundaryNeedsProps, GetProps<C>>>>(
    WrappedComponent: C
  ) => {
    WrappedComponent.displayName =
      displayName ??
      (WrappedComponent.displayName || WrappedComponent.name || 'Component')

    const _WithErrorBoundary = React.forwardRef(
      (props: GetProps<C> & WithErrorBoundaryNeedsProps, ref) => {
        const handleError = useMemo(
          () => (error: Error, errorInfo: React.ErrorInfo) => {
            const { onError, ...forwardProps } = props
            try {
              logger.error(
                `Error caught in function: ${
                  WrappedComponent.displayName as string
                }`,
                error,
                errorInfo
              )
              onError?.(error, errorInfo, forwardProps)
              errorCallback?.(error, errorInfo.componentStack, forwardProps)
            } catch (e) {
              logger.error(
                `handleError Error in ${
                  WrappedComponent.displayName as string
                }: `,
                e
              )
            }
          },
          [props]
        )

        let errorBoundaryProps:
          | ErrorBoundaryPropsWithComponent
          | ErrorBoundaryPropsWithRender = {
          FallbackComponent: ErrorFallback,
        }

        if (FallbackRender) {
          errorBoundaryProps = {
            fallbackRender: () => <FallbackRender {...props} />,
          }
        }

        return (
          <ErrorBoundary {...errorBoundaryProps} onError={handleError}>
            {/* @ts-expect-error */}
            <WrappedComponent {...props} ref={ref} />
          </ErrorBoundary>
        )
      }
    )

    const WithErrorBoundary: typeof _WithErrorBoundary & {
      WrappedComponent?: C
    } = _WithErrorBoundary
    WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName})`
    addWrappedComponent(WithErrorBoundary, WrappedComponent)
    return hoistStatics(WithErrorBoundary, WrappedComponent)
  }
function addWrappedComponent<WEB extends { WrappedComponent?: WC }, WC>(
  WithErrorBoundary: WEB,
  WrappedComponent: WC
): asserts WithErrorBoundary is WEB & { WrappedComponent: WC } {
  WithErrorBoundary.WrappedComponent = WrappedComponent
}
